// v3/app/router/routes.tsx — All v3 route definitions
// All routes under /v3 prefix.
// Uses React.lazy for code splitting on page components.
// Zero imports from v1/v2.

import React, { Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';
import { FeatureRoute } from './FeatureRoute';
import { AppLayout } from '../../layouts/AppLayout';
import { AuthLayout } from '../../layouts/AuthLayout';
import { Skeleton } from '../../design-system/primitives/skeleton';

// ─── Lazy-loaded page components ─────────────────────────────────────────────

const LoginPage = React.lazy(
  () => import('../../features/auth/LoginPage'),
);
const DashboardPage = React.lazy(
  () => import('../../features/dashboard/DashboardPage'),
);
const LeadsListPage = React.lazy(
  () => import('../../features/leads/LeadsListPage'),
);
const LeadCreatePage = React.lazy(
  () => import('../../features/leads/LeadCreatePage'),
);
const LeadDetailPage = React.lazy(
  () => import('../../features/leads/LeadDetailPage'),
);
const ApplicationsListPage = React.lazy(
  () => import('../../features/applications/ApplicationsListPage'),
);
const ApplicationCreatePage = React.lazy(
  () => import('../../features/applications/ApplicationCreatePage'),
);
const ApplicationDetailPage = React.lazy(
  () => import('../../features/applications/ApplicationDetailPage'),
);
const RVFormPage = React.lazy(
  () => import('../../features/residence-verification/RVFormPage'),
);
const RVViewPage = React.lazy(
  () => import('../../features/residence-verification/RVViewPage'),
);
const GroupsListPage = React.lazy(
  () => import('../../features/groups/GroupsListPage'),
);
const GroupCreatePage = React.lazy(
  () => import('../../features/groups/GroupCreatePage'),
);
const GroupDetailPage = React.lazy(
  () => import('../../features/groups/GroupDetailPage'),
);
const TasksListPage = React.lazy(
  () => import('../../features/tasks/TasksListPage'),
);
const TaskCreatePage = React.lazy(
  () => import('../../features/tasks/TaskCreatePage'),
);
const TaskDetailPage = React.lazy(
  () => import('../../features/tasks/TaskDetailPage'),
);
const LoginHistoryPage = React.lazy(
  () => import('../../features/login-history/LoginHistoryPage'),
);
const SettingsPage = React.lazy(
  () => import('../../features/settings/SettingsPage'),
);
const EmiCalculatorPage = React.lazy(
  () => import('../../features/emi-calculator/EmiCalculatorPage'),
);

// ─── Loading fallback ────────────────────────────────────────────────────────

function PageLoader() {
  return (
    <div className="p-6 space-y-4">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

// ─── Route definitions ───────────────────────────────────────────────────────

export function V3Routes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public: Login */}
        <Route element={<AuthLayout />}>
          <Route path="login" element={<LoginPage />} />
        </Route>

        {/* Protected routes — require authentication */}
        <Route element={<ProtectedRoute />}>
            <Route element={<AppLayout />}>
            {/* Dashboard — accessible to all authenticated users */}
            <Route element={<FeatureRoute featureId="dashboard" />}>
              <Route index element={<DashboardPage />} />
            </Route>

            {/* Leads */}
            <Route element={<FeatureRoute featureId="leads" />}>
              <Route path="leads" element={<LeadsListPage />} />
              <Route path="leads/create" element={<LeadCreatePage />} />
              <Route path="leads/:phoneNumber" element={<LeadDetailPage />} />
            </Route>

            {/* Applications */}
            <Route element={<FeatureRoute featureId="applications" />}>
              <Route path="applications" element={<ApplicationsListPage />} />
              <Route
                path="applications/create/:leadId"
                element={<ApplicationCreatePage />}
              />
              <Route
                path="applications/:applicationId"
                element={<ApplicationDetailPage />}
              />
            </Route>

            {/* Residence Verification (nested under applications) */}
            <Route element={<FeatureRoute featureId="residence-verification" />}>
              <Route
                path="applications/:applicationId/residence-verification"
                element={<RVFormPage />}
              />
              <Route
                path="applications/:applicationId/residence-verification/view"
                element={<RVViewPage />}
              />
            </Route>

            {/* Groups */}
            <Route element={<FeatureRoute featureId="groups" />}>
              <Route path="groups" element={<GroupsListPage />} />
              <Route path="groups/create" element={<GroupCreatePage />} />
              <Route path="groups/:id" element={<GroupDetailPage />} />
            </Route>

            {/* Tasks */}
            <Route element={<FeatureRoute featureId="tasks" />}>
              <Route path="tasks" element={<TasksListPage />} />
              <Route path="tasks/create" element={<TaskCreatePage />} />
              <Route path="tasks/:taskId" element={<TaskDetailPage />} />
            </Route>

            {/* Login History */}
            <Route element={<FeatureRoute featureId="login-history" />}>
              <Route path="login-history" element={<LoginHistoryPage />} />
            </Route>

            {/* Settings */}
            <Route element={<FeatureRoute featureId="settings" />}>
              <Route path="settings" element={<SettingsPage />} />
            </Route>

            {/* EMI Calculator */}
            <Route element={<FeatureRoute featureId="emi-calculator" />}>
              <Route path="emi-calculator" element={<EmiCalculatorPage />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}
